import {
  ColDef,
  ExcelExportParams,
  ExcelStyle,
  ProcessCellForExportParams
} from "ag-grid-enterprise";
import { ProjectSettingType } from "context/ProjectProvider";
import { ArrayStringCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import { AgGridReact } from "ag-grid-react";
import { getMatchingConditionalFormattingActions } from "components/conditional-formatting/apply-format-rules";
import {
  buildConditionalFormattingCellClassRules,
  buildConditionalFormattingExcelStyles,
  getConditionalFormattingExcelStyleId
} from "components/conditional-formatting/excel-styles";
import { normalizeConditionalFormattingRules } from "components/conditional-formatting/normalize-rules";
import type { ConditionalFormattingRule } from "components/conditional-formatting/types";
import { ExcelExportOptions } from "./excel-export-option";
import { formatLinkedDesignPackagesForExport } from "pages/preconstruction-log-page/hooks/linked-design-packages";
import {
  designScopeCsvDesignPackageContinuationRows,
  designScopeCsvFirstDesignPackage,
  designScopeLinkedPackagesForExport
} from "pages/design-scope-page/shared/design-scope-package-column-values";

// Re-export for existing tests / callers
export {
  buildConditionalFormattingExcelStyles,
  conditionalFormattingActionsToExcelStyle,
  getConditionalFormattingExcelStyleId
} from "components/conditional-formatting/excel-styles";

/** Remove control chars invalid in XML (keep tab, newline, CR). Prevents Excel "problem with some content" errors. */
const sanitizeForExcel = (val: unknown): string => {
  // Defensive guard: function is module-private and every caller passes a
  // guaranteed-present string (via `?? ""`, `|| ""`, `if (prependText)`, or
  // typed string sources), so null/undefined can never reach here.
  /* v8 ignore next */
  if (val === undefined || val === null) return "";
  const s = String(val).trim();
  return s
    .split("")
    .filter((c) => {
      const code = c.charCodeAt(0);
      return code >= 32 || code === 9 || code === 10 || code === 13;
    })
    .join("");
};

/** Escape double quotes for use inside Excel formula string (e.g. HYPERLINK). */
const escapeDoubleQuotes = (s: string): string => s.replace(/"/g, '""');

/** Excel sheet names: no \ / * ? : [ ], max 31 chars. */
const SANITIZE_SHEET_NAME_REGEX = /[\\/*?:[\]]/g;
const MAX_SHEET_NAME_LENGTH = 31;

export const sanitizeSheetName = (name: string): string => {
  const sanitized = (name || "Sheet")
    .replace(SANITIZE_SHEET_NAME_REGEX, " ")
    .trim();
  return sanitized.slice(0, MAX_SHEET_NAME_LENGTH) || "Sheet";
};

const getFormattedValues = (
  params: ProcessCellForExportParams<any>,
  options?: { appendDesignPackageRows?: boolean }
) => {
  const { value, column, formatValue, node, context } = params;
  const colDef = column.getColDef();

  const formattedValue = formatValue(value);

  const isHyperlink = (colDef.cellClassRules as any)?.hyperlinks?.() === true;

  let raw: string;
  switch (true) {
    case isHyperlink: {
      const url = sanitizeForExcel(
        `${window.location.href}/${node?.data?.id || ""}`
      );
      const linkText = sanitizeForExcel((formattedValue || value) ?? "");
      return `=HYPERLINK("${escapeDoubleQuotes(url)}", "${escapeDoubleQuotes(
        linkText
      )}")`;
    }
    case colDef.colId === "linked_design_packages":
      raw = formatLinkedDesignPackagesForExport(value);
      break;
    case colDef.colId === "linked_submittals":
    case colDef.colId === "linked_materials":
    case colDef.colId === "linked_tasks":
    case colDef.colId === "linked_activities":
    case colDef.colId === "locations":
    case colDef.colId === "project_locations":
    case colDef.cellRenderer === ArrayStringCellRenderer: {
      const arr = (value || []) as Array<{
        id: string;
        name: string;
        title: string;
        linked_submittals__title: string;
        deleted_from_source?: boolean;
      }>;

      const formattedItems = arr
        .filter((item) => !item.deleted_from_source)
        .map((item) =>
          sanitizeForExcel(
            item.name || item.title || item.linked_submittals__title || ""
          )
        );
      raw = formattedItems.join("\n");
      break;
    }
    case colDef.colId === "not_found_in_source":
      raw = value ? "Missing" : "";
      break;
    case colDef.colId === "review_response":
    case colDef.colId === "final_response": {
      const items = (value || []) as Array<{
        response: string | null;
        user_id: string | null;
        user_source_info_id: string | null;
        username: string | null;
      }>;
      const formattedItems = items.map((item) => {
        const response = item?.response || null;
        const userName = item?.username || "";
        return userName ? `${response || ""} (${userName})` : response || "";
      });
      return formattedItems.join("\n");
    }
    default: {
      if (
        options?.appendDesignPackageRows &&
        colDef.colId === "designPackage"
      ) {
        const firstPackage = designScopeCsvFirstDesignPackage({
          data: node?.data,
          context
        });
        if (firstPackage !== undefined) {
          raw = firstPackage;
          break;
        }
      }
      raw = String(
        designScopeLinkedPackagesForExport({
          colId: colDef.colId,
          value: (formattedValue || value) ?? "",
          data: node?.data,
          context
        }) ?? ""
      );
    }
  }
  return sanitizeForExcel(raw);
};

type TSortPosition = {
  colId: string;
  dependentColId: string;
  position: "before" | "after";
};

const formatGroupHeaderForKey = (key: any): string => {
  let s: string;
  if (key === undefined || key === null || key === "") {
    s = "(Blanks)";
  } else if (typeof key === "object" && !Array.isArray(key)) {
    s = key?.name ?? key?.title ?? String(key);
  } else if (Array.isArray(key)) {
    s = key.length
      ? key
          .map((v) =>
            typeof v === "object"
              ? (v?.name ?? v?.title ?? String(v))
              : String(v)
          )
          .join(", ")
      : "(Blanks)";
  } else {
    s = String(key);
  }
  return sanitizeForExcel(s);
};

const getRowGroupingConfig = (
  groupByField: string | undefined
): Record<string, unknown> => {
  const isGroupingActive = groupByField && groupByField !== "none";

  if (!isGroupingActive) {
    return {};
  }

  return {
    skipRowGroups: false,
    processRowGroupCallback(params: { node: { key: any } }): string {
      return formatGroupHeaderForKey(params.node?.key);
    }
  };
};

const resolveConditionalFormattingRules = (props: {
  grid: AgGridReact<any>;
  conditionalsFormatting?: ConditionalFormattingRule[];
}): ConditionalFormattingRule[] => {
  if (props.conditionalsFormatting) {
    return normalizeConditionalFormattingRules(props.conditionalsFormatting);
  }
  const context = props.grid.api?.getGridOption?.("context") as
    | { conditionalFormattingRules?: ConditionalFormattingRule[] }
    | undefined;
  return normalizeConditionalFormattingRules(
    context?.conditionalFormattingRules ?? []
  );
};

const withConditionalFormattingTableOptions = (
  options: ExcelExportOptions,
  hasConditionalFormatting: boolean
): ExcelExportOptions => {
  if (!hasConditionalFormatting) return options;

  const { exportAsExcelTable } = options;
  if (!exportAsExcelTable) return options;

  // Excel Table themes (row stripes) visually override cell fills/fonts.
  const tableConfig =
    typeof exportAsExcelTable === "object" && exportAsExcelTable !== null
      ? exportAsExcelTable
      : {};

  return {
    ...options,
    exportAsExcelTable: {
      ...tableConfig,
      showRowStripes: false,
      showColumnStripes: false
    }
  };
};

const getParams = (props: {
  projectDetails: ProjectSettingType;
  filename: string;
  prependText?: string;
  grid: AgGridReact<any>;
  includedColumns: Array<string>;
  excludedColumns: Array<string>;
  sortPosition?: Array<TSortPosition>;
  groupByField?: string;
  options?: ExcelExportOptions;
  conditionalsFormatting?: ConditionalFormattingRule[];
  /** Design Scope: one following row per extra design package. */
  appendDesignPackageRows?: boolean;
}): {
  exportParams: ExcelExportParams;
  conditionalFormattingExcelStyles: ExcelStyle[];
  conditionalFormattingRules: ConditionalFormattingRule[];
} => {
  const {
    projectDetails,
    filename,
    prependText,
    grid,
    includedColumns,
    excludedColumns,
    sortPosition = [],
    groupByField,
    options: rawOptions = {},
    conditionalsFormatting,
    appendDesignPackageRows = false
  } = props;

  const conditionalFormattingRules = resolveConditionalFormattingRules({
    grid,
    conditionalsFormatting
  });
  const conditionalFormattingExcelStyles =
    buildConditionalFormattingExcelStyles(conditionalFormattingRules);
  const options = withConditionalFormattingTableOptions(
    rawOptions,
    conditionalFormattingExcelStyles.length > 0
  );

  const { freezeHeaders, freezeColumns, exportAsExcelTable } = options;
  const { api } = grid;

  const wantsExcelTable =
    exportAsExcelTable === true ||
    (typeof exportAsExcelTable === "object" && exportAsExcelTable !== null);

  const allColumns = api?.getAllGridColumns() ?? [];

  // Determine columns to include for grouping
  const isGroupingActive = groupByField && groupByField !== "none";
  let effectiveIncludedColumns = [...includedColumns];

  if (isGroupingActive) {
    effectiveIncludedColumns = [
      ...new Set([...includedColumns, "ag-Grid-AutoColumn", groupByField])
    ];
  }

  const columnKeys = allColumns
    .filter((col) => {
      const colId = col.getColId();
      if (effectiveIncludedColumns.includes(colId)) return true;

      if (excludedColumns.includes(colId)) return false;

      return col.isVisible();
    })
    .map((col) => col.getColId());

  sortPosition.forEach((sort) => {
    const dependentColumnIndex = columnKeys.indexOf(sort.dependentColId);
    if (dependentColumnIndex > -1) {
      columnKeys.splice(dependentColumnIndex + 1, 0, sort.colId);
    }
  });

  // Pre-calculate unique header names for Excel table export
  // This avoids issues with processHeaderCallback being called multiple times
  const uniqueHeaderMap = new Map<string, string>();
  if (wantsExcelTable && api) {
    const usedNames = new Set<string>();
    const baseCount = new Map<string, number>();

    columnKeys.forEach((colId) => {
      const column = api.getColumn(colId);
      if (!column) return;

      const originalParentColId = (
        column.getOriginalParent()?.getColGroupDef() as any
      )?.colId;

      const originalHeader =
        api.getDisplayNameForColumn(column, "header") ?? "";
      let baseHeader = originalHeader.trim() || "Column";
      if (originalParentColId === "linked_submittals") {
        baseHeader += " - Submittals";
      }

      const count = baseCount.get(baseHeader) ?? 0;
      baseCount.set(baseHeader, count + 1);

      let finalHeader = baseHeader;

      if (count > 0) {
        // Duplicate - append suffix
        let suffix = count + 1;
        finalHeader = `${baseHeader} ${suffix}`;
        while (usedNames.has(finalHeader)) {
          suffix += 1;
          finalHeader = `${baseHeader} ${suffix}`;
        }
      } else if (usedNames.has(finalHeader)) {
        // First occurrence but name collision
        let suffix = 2;
        finalHeader = `${baseHeader} ${suffix}`;
        while (usedNames.has(finalHeader)) {
          suffix += 1;
          finalHeader = `${baseHeader} ${suffix}`;
        }
      }

      usedNames.add(finalHeader);
      uniqueHeaderMap.set(colId, finalHeader);
    });
  }

  const exportParams: ExcelExportParams = {
    columnKeys,
    processCellCallback(cellParams): string {
      return getFormattedValues(cellParams, { appendDesignPackageRows });
    },
    processHeaderCallback(headerParams): string {
      const colId = headerParams.column.getColId();

      // If exporting as Excel table, use pre-calculated unique header
      if (wantsExcelTable) {
        return uniqueHeaderMap.get(colId) ?? colId;
      }

      // Otherwise return the original header name
      return (
        headerParams.api.getDisplayNameForColumn(
          headerParams.column,
          "header"
        ) ?? ""
      );
    },
    ...getRowGroupingConfig(groupByField),
    fileName: filename,
    sheetName: sanitizeSheetName(projectDetails.name),
    autoConvertFormulas: true,
    ...(appendDesignPackageRows
      ? {
          getCustomContentBelowRow(params) {
            const rows = designScopeCsvDesignPackageContinuationRows({
              data: params.node?.data,
              context: params.context,
              columnIds: columnKeys
            });
            if (!rows.length) return undefined;
            return rows.map((row) => ({
              cells: row.map((value) => ({
                data: {
                  type: "String" as const,
                  value: sanitizeForExcel(value)
                }
              }))
            }));
          }
        }
      : {})
  };

  // Add freezeColumns if needed
  if (freezeColumns) {
    exportParams.freezeColumns = "pinned";
  }

  if (prependText) {
    const mergeAcross = Math.max(0, columnKeys.length - 1);
    exportParams.prependContent = [
      {
        cells: [
          {
            styleId: "customHeader",
            data: {
              type: "String",
              value: sanitizeForExcel(prependText)
            },
            mergeAcross
          }
        ]
      }
    ];
  }

  // Add freezeRows with prepend-aware logic if needed
  if (freezeHeaders) {
    const prependedRowCount = Array.isArray(exportParams.prependContent)
      ? exportParams.prependContent.length
      : 0;

    if (prependedRowCount > 0) {
      let dataRowCount = 0;
      exportParams.freezeRows = ((_params: { node?: any }): boolean => {
        const { node } = _params;
        if (!node) {
          return true;
        }
        if (node.rowIndex !== null && node.rowIndex !== undefined) {
          dataRowCount = 1 + dataRowCount;
          return dataRowCount <= 1;
        }
        return false;
      }) as any;
    } else {
      exportParams.freezeRows = "headers";
    }
  }

  if (exportAsExcelTable !== undefined) {
    exportParams.exportAsExcelTable = exportAsExcelTable;
  }

  return {
    exportParams,
    conditionalFormattingExcelStyles,
    conditionalFormattingRules
  };
};

const normalizeCellClasses = (
  cellClass: ColDef["cellClass"] | undefined,
  params: any
): string[] => {
  if (!cellClass) return [];
  const resolved =
    typeof cellClass === "function" ? cellClass(params) : cellClass;
  if (!resolved) return [];
  return Array.isArray(resolved) ? resolved.map(String) : [String(resolved)];
};

type MutableColDef = {
  cellClass?: ColDef["cellClass"];
  cellClassRules?: ColDef["cellClassRules"];
  colId?: string;
  field?: string;
};

export type ExcelExportConfig = {
  projectDetails: ProjectSettingType;
  filename: string;
  gridRef: React.RefObject<AgGridReact<any>>;
  prependText?: string;
  includedColumns?: Array<string>;
  excludedColumns?: Array<string>;
  sortPosition?: Array<TSortPosition>;
  groupByField?: string;
  options?: ExcelExportOptions;
  /** Optional override; defaults to grid context.conditionalFormattingRules */
  conditionalsFormatting?: ConditionalFormattingRule[];
  /** Design Scope: one following row per extra design package. */
  appendDesignPackageRows?: boolean;
};

export const exportEntityLogAsExcel = (config: ExcelExportConfig) => {
  const {
    gridRef,
    projectDetails,
    prependText,
    filename,
    includedColumns = [],
    excludedColumns = [],
    sortPosition,
    groupByField,
    options = {},
    conditionalsFormatting,
    appendDesignPackageRows
  } = config;

  if (!gridRef.current) return;

  const api = gridRef.current.api;
  const {
    exportParams,
    conditionalFormattingExcelStyles,
    conditionalFormattingRules
  } = getParams({
    projectDetails,
    filename,
    prependText,
    grid: gridRef.current,
    includedColumns,
    excludedColumns,
    sortPosition,
    groupByField,
    options,
    conditionalsFormatting,
    appendDesignPackageRows
  });

  // AG Grid 36 maps Excel styles from colDef cellClass / cellClassRules whose
  // ids exist in excelStyles. Inject CF styles + class rules for this export,
  // then restore. excelStyles is an Initial grid option, so set via cast.
  const previousExcelStyles = api.getGridOption?.("excelStyles" as never) as
    | ExcelStyle[]
    | undefined;
  const setExcelStylesOption = (styles: ExcelStyle[] | undefined) => {
    api.setGridOption?.("excelStyles" as never, styles as never);
  };
  const shouldInjectCfStyles = conditionalFormattingExcelStyles.length > 0;
  const columnPatches: Array<{
    colDef: MutableColDef;
    cellClass: ColDef["cellClass"] | undefined;
    cellClassRules: ColDef["cellClassRules"] | undefined;
  }> = [];

  if (shouldInjectCfStyles) {
    const baseStyles =
      previousExcelStyles && previousExcelStyles.length > 0
        ? previousExcelStyles
        : (excelStyles ?? []);
    // Deduplicate by id so React-merged CF styles aren't doubled.
    const byId = new Map<string, ExcelStyle>();
    [...baseStyles, ...conditionalFormattingExcelStyles].forEach((style) => {
      byId.set(style.id, style);
    });
    setExcelStylesOption(Array.from(byId.values()));

    const cfCellClassRules = buildConditionalFormattingCellClassRules(
      conditionalFormattingRules,
      conditionalFormattingExcelStyles.map((style) => style.id)
    );

    (api.getAllGridColumns?.() ?? []).forEach((column) => {
      const colDef = column.getColDef?.() as MutableColDef | undefined;
      if (!colDef) return;

      const originalCellClass = colDef.cellClass;
      const originalCellClassRules = colDef.cellClassRules;
      columnPatches.push({
        colDef,
        cellClass: originalCellClass,
        cellClassRules: originalCellClassRules
      });

      colDef.cellClassRules = {
        ...originalCellClassRules,
        ...cfCellClassRules
      };

      colDef.cellClass = (params: any) => {
        const classes = normalizeCellClasses(originalCellClass, params);
        const columnId =
          (typeof column.getColId === "function" ? column.getColId() : "") ||
          colDef.colId ||
          colDef.field ||
          "";
        const matchedActions = getMatchingConditionalFormattingActions({
          columnId,
          value: params?.value,
          rules: conditionalFormattingRules,
          node: params?.node
        });
        if (matchedActions) {
          const styleId = getConditionalFormattingExcelStyleId(matchedActions);
          if (
            conditionalFormattingExcelStyles.some(
              (style) => style.id === styleId
            )
          ) {
            classes.push(styleId);
          }
        }
        return classes;
      };
    });
  }

  try {
    api.exportDataAsExcel(exportParams);
  } finally {
    columnPatches.forEach(({ colDef, cellClass, cellClassRules }) => {
      colDef.cellClass = cellClass;
      colDef.cellClassRules = cellClassRules;
    });
    if (shouldInjectCfStyles) {
      setExcelStylesOption(previousExcelStyles);
    }
  }
};
